// Homepage content (admin: Website → Homepage). The homepage sections read their
// text and photos from here instead of the locale files:
//   const { c, isFR } = useHomeContent();   →  c.hero.title, c.tiers.items …
// Empty admin fields fall back to src/data/homeDefaults (today's content).
import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SITE_PAGES from "../data/generated/sitePages.json";
import HOME_DEFAULTS from "../data/homeDefaults";
import localizeServicePage from "../utils/localizeServicePage";
import fill from "../utils/fillDefaults";
import { resolvePromises } from "../utils/promises";
import { useEd } from "./Editable";

const HomeCtx = createContext(null);

// Service-promise tokens ({urgent}, {quoteReply}, …) are filled in (no city: the homepage).
// `raw`: keep the tokens (the on-page editor, so editors keep them).
// The published page (and no page) is resolved once per language; drafts every time.
const cache = {};
export function resolveHome(page, isFR, { raw = false } = {}) {
  if (page && page !== SITE_PAGES.home) return resolveHomeUncached(page, isFR, raw);
  const key = `${page ? "pub" : "none"}:${isFR ? "fr" : "en"}${raw ? ":raw" : ""}`;
  if (!cache[key]) cache[key] = resolveHomeUncached(page, isFR, raw);
  return cache[key];
}

function resolveHomeUncached(page, isFR, raw) {
  const defaults = isFR ? HOME_DEFAULTS.fr : HOME_DEFAULTS.en;
  const c = page ? fill(localizeServicePage(page, isFR).content, defaults) : defaults;
  return raw ? c : resolvePromises(c, { lang: isFR ? "fr" : "en" });
}

/** Provides the homepage content. `page` = admin draft (editor) or published page. */
export function HomeContentProvider({ page, isFR, children }) {
  const { editing } = useEd();
  const value = useMemo(() => {
    const p = page === undefined ? SITE_PAGES.home : page;
    const raw = { raw: editing };
    // Shared (both-language) values such as phone numbers live on the English side.
    const en = isFR ? resolveHome(p, false, raw) : null;
    return { c: resolveHome(p, isFR, raw), en: en || resolveHome(p, false, raw), isFR };
  }, [page, isFR, editing]);
  return <HomeCtx.Provider value={value}>{children}</HomeCtx.Provider>;
}

/** { c, en, isFR } — c = content in the page language, en = English content (shared values). */
export function useHomeContent() {
  const ctx = useContext(HomeCtx);
  const { pathname } = useLocation();
  if (ctx) return ctx;
  const isFR = pathname.startsWith("/fr");
  const page = SITE_PAGES.home;
  return { c: resolveHome(page, isFR), en: resolveHome(page, false), isFR };
}
